import React, { useState, useEffect } from 'react';
import {
  Activity, Search, Filter, Calendar, User, Package,
  ShoppingCart, Download, RefreshCw, Monitor, Smartphone, Printer as PrinterIcon,
  ChevronLeft, ChevronRight, ArrowRight, Hash, Globe, Database
} from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';
import CollapsibleFilters from './CollapsibleFilters';

interface AssignmentLog {
  id: string;
  entity_type: 'equipment' | 'consumable' | 'equipment_category' | 'consumable_category' | 'mobile_phone' | 'printer' | 'computer' | 'monitor';
  entity_id: number;
  action: string;
  old_values: any;
  new_values: any;
  user_id: number;
  username: string;
  user_name: string;
  ip_address: string;
  source_type?: string;
  created_at: string;
}

const AssignmentLogsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [logs, setLogs] = useState<AssignmentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedLog, setSelectedLog] = useState<AssignmentLog | null>(null);

  const entityTypeLabels: Record<string, string> = {
    equipment: t('assignlog.entityEquipment'),
    consumable: t('assignlog.entityConsumable'),
    equipment_category: t('assignlog.entityEquipmentCategory'),
    consumable_category: t('assignlog.entityConsumableCategory'),
    mobile_phone: t('assignlog.entityMobilePhone'),
    printer: t('assignlog.entityPrinter'),
    computer: t('assignlog.entityComputer'),
    monitor: t('assignlog.entityMonitor')
  };

  // Sadece zimmetleme işlemleri
  const actionLabels: Record<string, string> = {
    assign: t('assignlog.actionAssign'),
    unassign: t('assignlog.actionUnassign'),
    return: t('assignlog.actionReturn'),
    transfer: t('assignlog.actionTransfer')
  };

  // Sadece zimmetleme işlemleri için renkler
  const actionColors: Record<string, string> = {
    assign: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    unassign: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    return: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    transfer: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
  };

  // Alan adları çevirisi
  const fieldLabels: Record<string, string> = {
    assigned_user_id: t('assignlog.fieldAssignedUserId'),
    assigned_user_name: t('assignlog.fieldAssignedUserName'),
    unassigned_user_id: t('assignlog.fieldUnassignedUserId'),
    unassigned_user_name: t('assignlog.fieldUnassignedUserName'),
    unassigned_by_name: t('assignlog.fieldUnassignedByName'),
    assigned_by_name: t('assignlog.fieldAssignedByName'),
    user_id: t('assignlog.fieldUserId'),
    user_name: t('assignlog.fieldUserName'),
    username: t('assignlog.fieldUserName'),
    name: t('assignlog.fieldName'),
    serial_number: t('assignlog.fieldSerialNumber'),
    barcode: t('assignlog.fieldBarcode'),
    department: t('assignlog.fieldDepartment'),
    department_name: t('assignlog.fieldDepartment'),
    location: t('assignlog.fieldLocation'),
    status: t('assignlog.fieldStatus'),
    notes: t('assignlog.fieldNotes'),
    note: t('assignlog.fieldNote'),
    reason: t('assignlog.fieldReason'),
    brand: t('assignlog.fieldBrand'),
    model: t('assignlog.fieldModel'),
    device_brand: t('assignlog.fieldDeviceBrand'),
    device_model: t('assignlog.fieldDeviceModel'),
    computer_brand: t('assignlog.fieldComputerBrand'),
    computer_model: t('assignlog.fieldComputerModel'),
    equipment_brand: t('assignlog.fieldEquipmentBrand'),
    equipment_model: t('assignlog.fieldEquipmentModel'),
    assignment_type: t('assignlog.fieldAssignmentType'),
    condition_on_assignment: t('assignlog.fieldConditionOnAssignment'),
    imei: t('assignlog.fieldImei'),
    phone_number: t('assignlog.fieldPhoneNumber'),
    line_number: t('assignlog.fieldLineNumber'),
    gsm_number: t('assignlog.fieldGsmNumber'),
    ip_address: t('assignlog.fieldIpAddress'),
    mac_address: t('assignlog.fieldMacAddress'),
    hostname: t('assignlog.fieldHostname'),
    computer_name: t('assignlog.fieldHostname'),
    equipment_name: t('assignlog.fieldEquipmentName'),
    inventory_number: t('assignlog.fieldInventoryNumber'),
    category: t('assignlog.fieldCategory'),
    category_name: t('assignlog.fieldCategory'),
    quantity: t('assignlog.fieldQuantity'),
    unit: t('assignlog.fieldUnit'),
    warranty_end: t('assignlog.fieldWarrantyEnd'),
    purchase_date: t('assignlog.fieldPurchaseDate'),
    assigned_at: t('assignlog.fieldAssignedAt'),
    unassigned_at: t('assignlog.fieldUnassignedAt'),
    created_at: t('assignlog.fieldCreatedAt'),
    updated_at: t('assignlog.fieldUpdatedAt'),
    from_user: t('assignlog.fieldFromUser'),
    to_user: t('assignlog.fieldToUser'),
    from_user_id: t('assignlog.fieldFromUserId'),
    to_user_id: t('assignlog.fieldToUserId'),
    from_user_name: t('assignlog.fieldFromUser'),
    to_user_name: t('assignlog.fieldToUser'),
    previous_user: t('assignlog.fieldPreviousUser'),
    new_user: t('assignlog.fieldNewUser')
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'computer':
        return <Monitor className="w-4 h-4 text-primary" />;
      case 'monitor':
        return <Monitor className="w-4 h-4 text-blue-500" />;
      case 'mobile_phone':
        return <Smartphone className="w-4 h-4 text-green-600" />;
      case 'printer':
        return <PrinterIcon className="w-4 h-4 text-purple-600" />;
      case 'equipment':
      case 'equipment_category':
        return <Package className="w-4 h-4 text-orange-600" />;
      case 'consumable':
      case 'consumable_category':
        return <ShoppingCart className="w-4 h-4 text-teal-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, [currentPage, searchTerm, selectedEntityType, selectedAction, selectedUser, dateFrom, dateTo]);

  const fetchLogs = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '50',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedEntityType && { entity_type: selectedEntityType }),
        ...(selectedAction && { action: selectedAction }),
        ...(selectedUser && { user_id: selectedUser }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      });

      const response = await fetch(`/api/assignment-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalCount(data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Loglar yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : (data.data || []));
      }
    } catch (error) {
      console.error('Kullanıcılar yükleme hatası:', error);
    }
  };

  const exportLogs = async () => {
    try {
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(selectedEntityType && { entity_type: selectedEntityType }),
        ...(selectedAction && { action: selectedAction }),
        ...(selectedUser && { user_id: selectedUser }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      });

      const response = await fetch(`/api/assignment-logs/export?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `assignment_logs_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Log export hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: t('assignlog.exportError')
        }
      }));
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedEntityType('');
    setSelectedAction('');
    setSelectedUser('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const valueLabels: Record<string, string> = {
    // assignment_type
    permanent: t('assignlog.valuePermanent'),
    temporary: t('assignlog.valueTemporary'),
    // condition
    new: t('assignlog.valueNew'),
    good: t('assignlog.valueGood'),
    fair: t('assignlog.valueFair'),
    poor: t('assignlog.valuePoor'),
    damaged: t('assignlog.valueDamaged'),
    // status
    active: t('assignlog.valueActive'),
    passive: t('assignlog.valuePassive'),
    free: t('assignlog.valueFree'),
    assigned: t('assignlog.valueAssigned'),
    in_use: t('assignlog.valueInUse'),
    in_stock: t('assignlog.valueInStock'),
    maintenance: t('assignlog.valueMaintenance'),
    retired: t('assignlog.valueRetired'),
    lost: t('assignlog.valueLost'),
    stolen: t('assignlog.valueStolen'),
    broken: t('assignlog.valueBroken'),
    // boolean
    'true': t('assignlog.valueTrue'),
    'false': t('assignlog.valueFalse'),
    // source_type
    equipment_log: t('assignlog.valueEquipmentLog'),
    computer_history: t('assignlog.valueComputerHistory'),
    monitor_history: t('assignlog.valueMonitorHistory'),
    mobile_history: t('assignlog.valueMobileHistory'),
    // null
    'null': '-',
  };

  const translateValue = (val: any): string => {
    if (val === null || val === undefined || val === 'null') return '-';
    const s = String(val);
    return valueLabels[s] ?? valueLabels[s.toLowerCase()] ?? s;
  };

  const formatJsonValue = (value: any) => {
    if (!value) return '-';

    const translateKey = (key: string): string => fieldLabels[key] || key;
    const tv = (v: any) => valueLabels[String(v)] ?? valueLabels[String(v).toLowerCase()] ?? String(v);

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Object.entries(parsed)
          .filter(([_, val]) => val !== null && val !== undefined && val !== '' && val !== 'null')
          .map(([key, val]) => `${translateKey(key)}: ${tv(val)}`)
          .join(', ');
      } catch {
        return value;
      }
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .filter(([_, val]) => val !== null && val !== undefined && val !== '' && val !== 'null')
        .map(([key, val]) => `${translateKey(key)}: ${tv(val)}`)
        .join(', ');
    }
    return String(value);
  };

  const parseJsonValues = (val: any): Record<string, string> => {
    if (!val) return {};
    try {
      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
      if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {}
    return {};
  };

  const LogDetailModal = ({ log, onClose }: { log: AssignmentLog; onClose: () => void }) => {
    const oldVals = parseJsonValues(log.old_values);
    const newVals = parseJsonValues(log.new_values);

    const allKeys = Array.from(new Set([...Object.keys(oldVals), ...Object.keys(newVals)]));

    const renderValueSection = (title: string, values: Record<string, string>, colorClass: string, bgClass: string) => {
      const entries = Object.entries(values).filter(([_, v]) => v !== null && v !== undefined && v !== '' && v !== 'null');
      if (entries.length === 0) return null;
      return (
        <div className={`rounded-lg border ${bgClass} p-4`}>
          <h4 className={`text-sm font-semibold mb-3 ${colorClass}`}>{title}</h4>
          <div className="space-y-2">
            {entries.map(([key, val]) => (
              <div key={key} className="flex gap-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400 min-w-[140px] shrink-0">
                  {fieldLabels[key] || key}:
                </span>
                <span className="text-gray-900 dark:text-gray-100 break-all">{translateValue(val)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderCompareTable = () => {
      if (allKeys.length === 0) return null;
      const changed = allKeys.filter(k => String(oldVals[k] ?? '') !== String(newVals[k] ?? ''));
      if (changed.length === 0 && allKeys.length > 0) {
        return renderValueSection(t('assignlog.detailDetails'), newVals || oldVals, 'text-gray-700 dark:text-gray-300', 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600');
      }
      return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('assignlog.compareFieldHeader')}</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-red-500 dark:text-red-400 uppercase">{t('assignlog.comparePreviousHeader')}</th>
                <th className="px-3 py-2 text-center text-gray-400 w-6"><ArrowRight className="w-3 h-3 mx-auto" /></th>
                <th className="px-3 py-2 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase">{t('assignlog.compareNewHeader')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {allKeys.map(key => {
                const ov = translateValue(oldVals[key] ?? null);
                const nv = translateValue(newVals[key] ?? null);
                const isChanged = ov !== nv;
                return (
                  <tr key={key} className={isChanged ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                      {fieldLabels[key] || key}
                    </td>
                    <td className={`px-3 py-2 break-all ${isChanged ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {ov}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-300 dark:text-gray-600">→</td>
                    <td className={`px-3 py-2 break-all ${isChanged ? 'text-green-700 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                      {nv}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div className="space-y-5">
        {/* Özet bilgiler */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1">
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('assignlog.detailDateTime')}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1">
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><User className="w-3 h-3" /> {t('assignlog.detailPerformedBy')}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {log.user_name || log.username || t('assignlog.system')}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1">
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">{getEntityIcon(log.entity_type)} {t('assignlog.detailEntityType')}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {entityTypeLabels[log.entity_type] || log.entity_type}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1">
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Hash className="w-3 h-3" /> {t('assignlog.detailEntityId')}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">#{log.entity_id}</p>
          </div>
          {log.ip_address && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Globe className="w-3 h-3" /> {t('assignlog.detailIPAddress')}</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{log.ip_address}</p>
            </div>
          )}
          {log.source_type && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Database className="w-3 h-3" /> {t('assignlog.detailSource')}</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{translateValue(log.source_type)}</p>
            </div>
          )}
        </div>

        {/* İşlem tipi */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">{t('assignlog.detailAction')}</span>
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${actionColors[log.action] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
            {actionLabels[log.action] || log.action}
          </span>
        </div>

        {/* Değişiklik detayları */}
        {log.action === 'transfer' || log.action === 'update' ? (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('assignlog.detailChanges')}</h4>
            {renderCompareTable()}
          </div>
        ) : log.action === 'assign' ? (
          renderValueSection(t('assignlog.assignmentInfo'), newVals, 'text-green-700 dark:text-green-400', 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800')
        ) : log.action === 'unassign' ? (
          renderValueSection(t('assignlog.unassignInfo'), oldVals, 'text-red-600 dark:text-red-400', 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800')
        ) : log.action === 'return' ? (
          renderValueSection(t('assignlog.returnInfo'), oldVals, 'text-purple-700 dark:text-purple-400', 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800')
        ) : log.action === 'delete' ? (
          renderValueSection(t('assignlog.deletedRecord'), oldVals, 'text-red-600 dark:text-red-400', 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800')
        ) : log.action === 'create' ? (
          renderValueSection(t('assignlog.createdRecord'), newVals, 'text-green-700 dark:text-green-400', 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800')
        ) : (
          <div className="space-y-3">
            {Object.keys(oldVals).length > 0 && renderValueSection(t('assignlog.previousState'), oldVals, 'text-red-600 dark:text-red-400', 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800')}
            {Object.keys(newVals).length > 0 && renderValueSection(t('assignlog.newState'), newVals, 'text-green-700 dark:text-green-400', 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800')}
          </div>
        )}
      </div>
    );
  };

  // Mobile card component for each log
  const LogCard = ({ log }: { log: AssignmentLog }) => (
    <div
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3 cursor-pointer hover:border-primary hover:shadow-sm transition-all"
      onClick={() => setSelectedLog(log)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {getEntityIcon(log.entity_type)}
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {entityTypeLabels[log.entity_type] || log.entity_type}
          </span>
        </div>
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${actionColors[log.action] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
          {actionLabels[log.action] || log.action}
        </span>
      </div>

      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
        <Calendar className="w-4 h-4 mr-2" />
        {new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
      </div>

      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
        <User className="w-4 h-4 mr-2" />
        {log.user_name || log.username || t('assignlog.system')}
      </div>

      {(log.old_values || log.new_values) && (
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          {log.action === 'create' && log.new_values && (
            <div className="text-sm text-green-700 dark:text-green-400">
              <strong>{t('assignlog.cardNew')}</strong> {formatJsonValue(log.new_values)}
            </div>
          )}
          {log.action === 'update' && (
            <>
              {log.old_values && (
                <div className="text-sm text-red-700 dark:text-red-400 mb-1">
                  <strong>{t('assignlog.cardOld')}</strong> {formatJsonValue(log.old_values)}
                </div>
              )}
              {log.new_values && (
                <div className="text-sm text-green-700 dark:text-green-400">
                  <strong>{t('assignlog.cardNew')}</strong> {formatJsonValue(log.new_values)}
                </div>
              )}
            </>
          )}
          {log.action === 'assign' && (
            <div className="text-sm text-green-700 dark:text-green-400">
              <strong>{t('assignlog.cardAssigned')}</strong> {formatJsonValue(log.new_values)}
            </div>
          )}
          {log.action === 'unassign' && (
            <div className="text-sm text-red-700 dark:text-red-400">
              <strong>{t('assignlog.cardUnassigned')}</strong> {formatJsonValue(log.old_values)}
            </div>
          )}
          {log.action === 'return' && (
            <div className="text-sm text-purple-700 dark:text-purple-400">
              <strong>{t('assignlog.cardReturn')}</strong> {formatJsonValue(log.old_values)}
            </div>
          )}
          {log.action === 'transfer' && (
            <>
              {log.old_values && (
                <div className="text-sm text-red-700 dark:text-red-400 mb-1">
                  <strong>{t('assignlog.cardPrevious')}</strong> {formatJsonValue(log.old_values)}
                </div>
              )}
              {log.new_values && (
                <div className="text-sm text-green-700 dark:text-green-400">
                  <strong>{t('assignlog.cardNew')}</strong> {formatJsonValue(log.new_values)}
                </div>
              )}
            </>
          )}
          {log.action === 'delete' && log.old_values && (
            <div className="text-sm text-red-700 dark:text-red-400">
              <strong>{t('assignlog.cardDeleted')}</strong> {formatJsonValue(log.old_values)}
            </div>
          )}
        </div>
      )}

      {log.ip_address && (
        <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">
          IP: {log.ip_address}
        </div>
      )}
    </div>
  );

  return (
    <>
    {selectedLog && (
      <Modal isOpen={!!selectedLog} onClose={() => setSelectedLog(null)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                {getEntityIcon(selectedLog.entity_type)}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('assignlog.modalTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    {entityTypeLabels[selectedLog.entity_type] || selectedLog.entity_type} · #{selectedLog.entity_id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
          </div>
        </div>
      </Modal>
    )}
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('assignlog.title')}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('assignlog.subtitle')}
          </p>
        </div>
        <div className="flex flex-row gap-2">
          <button
            onClick={fetchLogs}
            className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('assignlog.refresh')}
          </button>
          <button
            onClick={exportLogs}
            className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('assignlog.export')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 xl:grid-cols-6 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('assignlog.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-3 py-2.5 w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="xl:col-span-5">
          <CollapsibleFilters
            activeCount={[selectedEntityType, selectedAction, selectedUser, dateFrom, dateTo].filter(Boolean).length}
            onClear={clearFilters}
            desktopClassName="md:grid md:grid-cols-3 xl:grid-cols-5 md:gap-3"
          >
          {/* Entity Type Filter */}
          <select
            value={selectedEntityType}
            onChange={(e) => { setSelectedEntityType(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">{t('assignlog.allTypes')}</option>
            {Object.entries(entityTypeLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* Action Filter */}
          <select
            value={selectedAction}
            onChange={(e) => { setSelectedAction(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">{t('assignlog.allActions')}</option>
            {Object.entries(actionLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* User Filter */}
          <select
            value={selectedUser}
            onChange={(e) => { setSelectedUser(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">{t('assignlog.allUsers')}</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>

          {/* Date From */}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />

          {/* Date To */}
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          </CollapsibleFilters>
          </div>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('assignlog.totalRecords', { count: totalCount })}
          </span>
          <button
            onClick={clearFilters}
            className="hidden md:flex items-center px-3 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
          >
            <Filter className="w-4 h-4 mr-2" />
            {t('assignlog.clearFilters')}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('assignlog.noLogs')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t('assignlog.noLogsDesc')}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile View - Cards */}
          <div className="lg:hidden space-y-3">
            {logs.map((log) => (
              <LogCard key={log.id} log={log} />
            ))}
          </div>

          {/* Desktop View - Table */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('assignlog.colDateTime')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('assignlog.colUser')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('assignlog.colType')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('assignlog.colAction')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('assignlog.colChanges')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('assignlog.colIP')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-600">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 cursor-pointer" onClick={() => setSelectedLog(log)}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          {new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                          <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          {log.user_name || log.username || t('assignlog.system')}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                          {getEntityIcon(log.entity_type)}
                          <span className="ml-2">{entityTypeLabels[log.entity_type] || log.entity_type}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${actionColors[log.action] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 dark:text-gray-100 max-w-md truncate">
                          {log.action === 'create' && log.new_values && (
                            <span className="text-green-700 dark:text-green-400">
                              {formatJsonValue(log.new_values)}
                            </span>
                          )}
                          {log.action === 'update' && (
                            <span>
                              {log.old_values && (
                                <span className="text-red-700 dark:text-red-400">
                                  {formatJsonValue(log.old_values)}
                                </span>
                              )}
                              {log.old_values && log.new_values && ' → '}
                              {log.new_values && (
                                <span className="text-green-700 dark:text-green-400">
                                  {formatJsonValue(log.new_values)}
                                </span>
                              )}
                            </span>
                          )}
                          {log.action === 'assign' && (
                            <span className="text-green-700 dark:text-green-400">
                              <strong>{t('assignlog.cardAssigned')}</strong> {formatJsonValue(log.new_values)}
                            </span>
                          )}
                          {log.action === 'unassign' && (
                            <span className="text-red-700 dark:text-red-400">
                              <strong>{t('assignlog.cardUnassigned')}</strong> {formatJsonValue(log.old_values)}
                            </span>
                          )}
                          {log.action === 'return' && (
                            <span className="text-purple-700 dark:text-purple-400">
                              <strong>{t('assignlog.cardReturn')}</strong> {formatJsonValue(log.old_values)}
                            </span>
                          )}
                          {log.action === 'transfer' && (
                            <span>
                              {log.old_values && (
                                <span className="text-red-700 dark:text-red-400">
                                  <strong>{t('assignlog.cardPrevious')}</strong> {formatJsonValue(log.old_values)}
                                </span>
                              )}
                              {log.old_values && log.new_values && ' → '}
                              {log.new_values && (
                                <span className="text-green-700 dark:text-green-400">
                                  <strong>{t('assignlog.cardNew')}</strong> {formatJsonValue(log.new_values)}
                                </span>
                              )}
                            </span>
                          )}
                          {log.action === 'delete' && log.old_values && (
                            <span className="text-red-700 dark:text-red-400">
                              {formatJsonValue(log.old_values)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {log.ip_address || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('assignlog.pageOf', { current: currentPage, total: totalPages })}
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="hidden sm:flex items-center space-x-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                          currentPage === pageNum
                            ? 'bg-primary text-white'
                            : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
};

export default AssignmentLogsPage;
